/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { ComponentChildren, VNode } from "preact";
import { MerchantAuthMethod, Duration, TalerMerchantApi } from "@gnu-taler/taler-util";
import { loginTokenLifetimeMinutes } from "../stores/devSettings.js";
import {
  merchantManagementClient,
  sendTanChallenge,
  confirmTanChallenge,
  purgeChallengeIds,
} from "../api/client.js";
import { authenticate } from "./SignInRoute.js";
import {
  SelfProvisionScreen,
  type ProvisionAttempt,
  type ProvisionRequest,
  type SelfProvisionScreenProps,
} from "../screens/SelfProvisionScreen.js";
import { configurationFailure, normalizeApiFailure } from "../api/failure.js";
import { useTranslation } from "../context/translation.js";
import type { TranslateFn } from "../context/translation.js";
import { createPasswordVerifier } from "../stores/session.js";
import { parseChallengeResponse } from "../utils/challengeRounds.js";
import { useMerchantConfig } from "../api/hooks/useMerchantConfig.js";
import { InitialLoadingState } from "../ui/InitialLoadingState.js";
import { FooterControls } from "../ui/FooterControls.js";
import { TalerLogo } from "../ui/TalerLogo.js";
import { ReadErrorBanner } from "../ui/ReadErrorBanner.js";

/**
 * The challenges a refused login-token request is waiting on.
 *
 * `POST /private/token` answers 202 with its own freshly minted challenges
 * (api-merchant.rst 4.1, since v21). Presenting the ones solved while creating
 * the account instead gets 410 Gone, because those are spent.
 */
function tokenChallenges(res: unknown): TalerMerchantApi.ChallengeResponse | undefined {
  const r = res as { case?: number; detail?: unknown };
  if (r.case !== 202) return undefined;
  return parseChallengeResponse(r.detail);
}

/** Trade the password for a login token, reporting a demand for a second factor. */
async function tokenFor(
  req: ProvisionRequest,
  challengeIds: string[] | undefined,
  t?: TranslateFn,
): Promise<ProvisionAttempt> {
  const signedIn = await authenticate({
    account: req.account,
    secret: req.password,
    backendUrl: req.backendUrl,
    challengeIds,
  }, t);
  if (signedIn.type === "ok") {
    return {
      type: "ok",
      token: signedIn.token,
      tokenInfo: signedIn.tokenInfo,
      passwordVerifier: signedIn.passwordVerifier,
    };
  }
  const challenges = tokenChallenges(signedIn);
  if (challenges) {
    return { type: "verify-token", challengeResponse: challenges };
  }
  // The account HAS been created; only the token was refused for some other
  // reason. Reporting a failure would strand the merchant on a form for an
  // account that already exists, so hand them to the sign-in screen. The
  // password is still never kept as the credential.
  return { type: "ok" };
}

/**
 * A documented failure carries `detail`; a 202 demanding a second factor
 * carries a codec-parsed `body`, and that is where the challenges are.
 */
function failureDetail(res: unknown): unknown {
  const r = res as { detail?: unknown; body?: unknown };
  return r.detail ?? r.body;
}

export async function provision(req: ProvisionRequest, t?: TranslateFn): Promise<ProvisionAttempt> {
  let rootUrl: URL;
  try {
    rootUrl = new URL(req.backendUrl);
  } catch (cause) {
    return {
      type: "fail",
      failure: configurationFailure(t ? t`The merchant backend URL is invalid.` : "The merchant backend URL is invalid.", cause),
    };
  }

  // Second round: the account exists and only the login token is outstanding,
  // so creating the account again would just collide with itself.
  if (req.tokenChallengeIds && req.tokenChallengeIds.length > 0) {
    try {
      const attempt = await tokenFor(req, req.tokenChallengeIds, t);
      if (attempt.type === "ok" && attempt.token) {
        purgeChallengeIds(req.tokenChallengeIds);
      }
      return attempt;
    } catch (cause) {
      return { type: "fail", failure: normalizeApiFailure(cause) };
    }
  }

  try {
    const client = merchantManagementClient({ rootUrl });
    const resp = await client.createInstanceSelfProvision(
      {
        id: req.account,
        name: req.businessName,
        auth: {
          method: MerchantAuthMethod.TOKEN,
          password: req.password,
        },
        use_stefan: true,
        email: req.email,
        phone_number: req.phone,
        address: {},
        jurisdiction: {},
      },
      req.challengeIds && req.challengeIds.length > 0
        ? {
            challengeIds: req.challengeIds,
            tokenValidity: Duration.fromMilliseconds(loginTokenLifetimeMinutes.value * 60 * 1000),
          }
        : { tokenValidity: Duration.fromMilliseconds(loginTokenLifetimeMinutes.value * 60 * 1000) },
    );

    if (resp.type === "fail") {
      return {
        type: "fail",
        case: resp.case as number,
        detail: failureDetail(resp),
        failure: normalizeApiFailure(resp),
      };
    }

    // The solved challenges are spent, so they must not be replayed.
    if (req.challengeIds && req.challengeIds.length > 0) {
      purgeChallengeIds(req.challengeIds);
    }

    // A 200 answers with a login token; a 204 answers with nothing. Either way
    // the chosen password must not become the stored credential — that is the
    // bug this whole path had, and the sign-in path had before it.
    const body = (resp as { body?: any }).body;
    if (body?.access_token) {
      const expiresS = body.expiration?.t_s;
      return {
        type: "ok",
        token: String(body.access_token),
        passwordVerifier: await createPasswordVerifier(req.password),
        tokenInfo: {
          expiresS: typeof expiresS === "number" ? expiresS : undefined,
          refreshable: body.refreshable === true,
          scope: typeof body.scope === "string" ? body.scope : undefined,
        },
      };
    }

    // Nothing was issued, so exchange the password for a token exactly as
    // signing in does, rather than keeping the password. The challenges solved
    // a moment ago are spent, so none are presented here; if the server wants a
    // second factor for the token it says so, with new ones.
    return await tokenFor(req, undefined, t);
  } catch (cause) {
    // An absent `case` says the request never landed, which is how the screen
    // tells a refusal from a connection problem.
    return { type: "fail", failure: normalizeApiFailure(cause) };
  }
}

export interface SignupPolicy {
  available: boolean;
  mandatoryTanChannels: ("email" | "sms")[];
}

/** Interpret public configuration conservatively: absent or false means closed. */
export function signupPolicy(config: {
  haveSelfProvisioning?: boolean;
  mandatoryTanChannels?: TalerMerchantApi.TanChannel[];
} | undefined): SignupPolicy {
  return {
    available: config?.haveSelfProvisioning === true,
    mandatoryTanChannels: (config?.mandatoryTanChannels ?? []).filter(
      (channel) => channel === TalerMerchantApi.TanChannel.EMAIL ||
        channel === TalerMerchantApi.TanChannel.SMS,
    ) as ("email" | "sms")[],
  };
}

function SignupGate({ children }: { children: ComponentChildren }): VNode {
  const { t } = useTranslation();
  return (
    <div class="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      <FooterControls />
      <div class="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <TalerLogo class="h-10 w-auto mx-auto text-taler-brand" />
        <div class="mt-6">{children}</div>
        <a href="#/signin" class="mt-4 inline-block text-xs font-semibold text-taler-brand hover:underline">
          {t`Merchant Portal Sign-In`}
        </a>
      </div>
    </div>
  );
}

export function SelfProvisionRoute(props: SelfProvisionScreenProps): VNode {
  const { t } = useTranslation();
  const { resource } = useMerchantConfig();
  if (resource.isLoading && !resource.data) {
    return (
      <SignupGate>
        <InitialLoadingState>{t`Loading…`}</InitialLoadingState>
      </SignupGate>
    );
  }
  if (resource.error) {
    return (
      <SignupGate>
        <ReadErrorBanner
          resource={resource}
          title={t`Merchant server configuration could not be loaded`}
        />
      </SignupGate>
    );
  }
  if (!resource.data) {
    return (
      <SignupGate>
        <p class="rounded-xl border border-gray-200 bg-white p-8 text-sm text-gray-600">
          {t`Merchant server configuration is unavailable.`}
        </p>
      </SignupGate>
    );
  }
  const policy = signupPolicy(resource.data);
  if (!policy.available) {
    return (
      <SignupGate>
        <p class="rounded-xl border border-gray-200 bg-white p-8 text-sm text-gray-600">
          {t`Registration is not available here.`}
        </p>
      </SignupGate>
    );
  }
  return (
    <SelfProvisionScreen
      {...props}
      mandatoryTanChannels={policy.mandatoryTanChannels}
      onProvision={(request) => provision(request, t)}
      onSendChallenge={async (challengeId, backendUrl) => {
        const rootUrl = new URL(backendUrl);
        const res = await sendTanChallenge({ rootUrl }, challengeId, false, t);
        if (!res.ok) throw res.failure ?? new Error(res.errorMsg);
        return res.retryAfter ?? 0;
      }}
      onSolveChallenge={async (challengeId, code, backendUrl) => {
        try {
          const rootUrl = new URL(backendUrl);
          return await confirmTanChallenge({ rootUrl }, challengeId, code, false, t);
        } catch (cause) {
          return { ok: false, failure: cause };
        }
      }}
    />
  );
}
